import React, { useState, useEffect } from 'react';
import { req } from '../services/api';
import Modal from '../components/Modal';

const money = value => Number(value || 0).toFixed(2) + ' €';

const formatDate = value => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('cs-CZ');
};

const normalize = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const [detailModal, setDetailModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);

  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setLoading(true);

    try {
      const data = await req('GET', '/customers');
      if (Array.isArray(data)) setCustomers(data);
    } catch (error) {
      console.error('Customers load failed:', error);
      alert(
        'Chyba p\u0159i na\u010d\u00edt\u00e1n\u00ed z\u00e1kazn\u00edk\u016f: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    } finally {
      setLoading(false);
    }
  };

  const q = normalize(search);

  const filtered = customers.filter(c => {
    const matchesSearch =
      normalize(c.user?.name).includes(q) ||
      normalize(c.user?.email).includes(q) ||
      normalize(c.user?.phone).includes(q);

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && !c.blocked) ||
      (statusFilter === 'blocked' && c.blocked);

    return matchesSearch && matchesStatus;
  });

  const totalSpent = customers.reduce(
    (sum, c) => sum + Number(c.totalSpent || 0),
    0
  );

  const totalCredits = customers.reduce(
    (sum, c) => sum + Number(c.creditBalance || 0),
    0
  );

  const totalRides = customers.reduce(
    (sum, c) => sum + Number(c.totalRides || 0),
    0
  );

  const blockedCount = customers.filter(c => c.blocked).length;

  const block = async (id, blocked) => {
    try {
      await req('PATCH', '/customers/' + id, { blocked });
      await loadCustomers();
    } catch (error) {
      alert(
        'Chyba p\u0159i zm\u011bn\u011b stavu z\u00e1kazn\u00edka: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const deleteAccount = async c => {
    if (
      !window.confirm(
        'Opravdu chcete odstranit \u00fa\u010det z\u00e1kazn\u00edka ' +
          c.user.name +
          ' z provozu? \u00da\u010det ji\u017e nebude mo\u017en\u00e9 pou\u017e\u00edvat, ale historie j\u00edzd a financ\u00ed z\u016fstane zachov\u00e1na.'
      )
    ) {
      return;
    }

    try {
      const result = await req('DELETE', '/users/' + c.user.id);

      if (result?.success) {
        alert('\u00da\u010det byl odstran\u011bn z provozu.');
        setDetailModal(false);
        await loadCustomers();
      } else {
        alert(
          'Chyba p\u0159i odstra\u0148ov\u00e1n\u00ed: ' +
            (result?.error || 'nezn\u00e1m\u00e1 chyba')
        );
      }
    } catch (error) {
      alert(
        'Chyba p\u0159i odstra\u0148ov\u00e1n\u00ed: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const openDetail = c => {
    setCurrentCustomer(c);
    setDetailModal(true);
  };

  const openEdit = c => {
    setCurrentCustomer(c);
    setEditForm({
      name: c.user?.name || '',
      email: c.user?.email || '',
      phone: c.user?.phone || ''
    });
    setEditModal(true);
  };

  const saveEdit = async () => {
    try {
      await req(
        'PATCH',
        '/customers/' + currentCustomer.id + '/edit',
        editForm
      );

      setEditModal(false);
      await loadCustomers();
      alert('\u2713 Z\u00e1kazn\u00edk byl upraven');
    } catch (error) {
      alert(
        'Chyba p\u0159i \u00faprav\u011b z\u00e1kazn\u00edka: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const openPassword = c => {
    setCurrentCustomer(c);
    setNewPassword('');
    setPasswordModal(true);
  };

  const savePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Heslo mus\u00ed m\u00edt alespo\u0148 6 znak\u016f');
      return;
    }

    try {
      await req(
        'PATCH',
        '/users/' + currentCustomer.user.id + '/password',
        { password: newPassword }
      );

      setPasswordModal(false);
      alert('\u2713 Heslo bylo zm\u011bn\u011bno');
    } catch (error) {
      alert(
        'Chyba p\u0159i zm\u011bn\u011b hesla: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  return (
    <div>
      <div className="page-title">Zákazníci</div>
      <div className="page-sub">
        Přehled zákazníků, jejich aktivity a útraty
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{customers.length}</div>
          <div className="stat-label">Celkem zákazníků</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{totalRides}</div>
          <div className="stat-label">Objednaných jízd</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{money(totalSpent)}</div>
          <div className="stat-label">Útrata zákazníků</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{money(totalCredits)}</div>
          <div className="stat-label">Kredit zákazníků</div>
        </div>

        <div className={blockedCount > 0 ? 'stat-card alert' : 'stat-card'}>
          <div className="stat-value">{blockedCount}</div>
          <div className="stat-label">Blokovaní</div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 14
        }}
      >
        <input
          className="search-bar"
          style={{ marginBottom: 0 }}
          placeholder="Hledat podle jména, e-mailu nebo telefonu..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <button
          className={
            'btn-sm ' +
            (statusFilter === 'all' ? 'btn-gold' : 'btn-gray')
          }
          onClick={() => setStatusFilter('all')}
        >
          Všichni
        </button>

        <button
          className={
            'btn-sm ' +
            (statusFilter === 'active' ? 'btn-green' : 'btn-gray')
          }
          onClick={() => setStatusFilter('active')}
        >
          Aktivní
        </button>

        <button
          className={
            'btn-sm ' +
            (statusFilter === 'blocked' ? 'btn-red' : 'btn-gray')
          }
          onClick={() => setStatusFilter('blocked')}
        >
          Blokovaní
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Zákazníci</span>
          <span style={{ color: 'var(--gray)', fontSize: 12 }}>
            Zobrazeno {filtered.length} z {customers.length}
          </span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Zákazník</th>
                <th>Telefon</th>
                <th>Jízdy</th>
                <th>Útrata</th>
                <th>Kredit</th>
                <th>Poslední jízda</th>
                <th>Registrace</th>
                <th>Jazyk</th>
                <th>Status</th>
                <th>Akce</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="loading-cell">
                    Načítám...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty-cell">
                    Žádní zákazníci
                  </td>
                </tr>
              ) : (
                filtered.map(c => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.user?.name || '—'}</strong>
                      <br />
                      <span
                        style={{
                          color: 'var(--gray)',
                          fontSize: 11
                        }}
                      >
                        {c.user?.email || '—'}
                      </span>
                    </td>

                    <td style={{ fontSize: 12 }}>
                      {c.user?.phone || '—'}
                    </td>

                    <td>
                      <strong>{c.totalRides || 0}</strong>
                      <br />
                      <span
                        style={{
                          color: 'var(--gray)',
                          fontSize: 11
                        }}
                      >
                        {c.completedRides || 0} dokončeno
                      </span>
                    </td>

                    <td
                      style={{
                        color: 'var(--gold)',
                        fontWeight: 700
                      }}
                    >
                      {money(c.totalSpent)}
                    </td>

                    <td>{money(c.creditBalance)}</td>

                    <td style={{ fontSize: 12 }}>
                      {formatDate(c.lastRideAt)}
                    </td>

                    <td
                      style={{
                        color: 'var(--gray)',
                        fontSize: 12
                      }}
                    >
                      {formatDate(c.createdAt)}
                    </td>

                    <td>
                      <span className="badge badge-gray">
                        {(c.user?.language || 'cs').toUpperCase()}
                      </span>
                    </td>

                    <td>
                      {c.blocked ? (
                        <span className="badge badge-red">
                          Blokován
                        </span>
                      ) : (
                        <span className="badge badge-green">
                          Aktivní
                        </span>
                      )}
                    </td>

                    <td>
                      <div className="actions">
                        <button
                          className="btn-sm btn-blue"
                          onClick={() => openDetail(c)}
                        >
                          Detail
                        </button>

                        <button
                          className="btn-sm btn-gold"
                          onClick={() => openEdit(c)}
                        >
                          Upravit
                        </button>

                        {!c.blocked ? (
                          <button
                            className="btn-sm btn-red"
                            onClick={() => block(c.id, true)}
                          >
                            Blokovat
                          </button>
                        ) : (
                          <button
                            className="btn-sm btn-green"
                            onClick={() => block(c.id, false)}
                          >
                            Odblokovat
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detailModal && currentCustomer && (
        <Modal
          title="Detail zákazníka"
          onClose={() => setDetailModal(false)}
        >
          <div className="payout-detail-box">
            <div className="payout-row">
              <span className="payout-row-label">Jméno</span>
              <span className="payout-row-value">
                {currentCustomer.user?.name || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">E-mail</span>
              <span className="payout-row-value">
                {currentCustomer.user?.email || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Telefon</span>
              <span className="payout-row-value">
                {currentCustomer.user?.phone || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Adresa</span>
              <span className="payout-row-value">
                {currentCustomer.user?.address || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Jazyk</span>
              <span className="payout-row-value">
                {(currentCustomer.user?.language || 'cs').toUpperCase()}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Celkem jízd</span>
              <span className="payout-row-value">
                {currentCustomer.totalRides || 0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Dokončené jízdy
              </span>
              <span className="payout-row-value">
                {currentCustomer.completedRides || 0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Celková útrata
              </span>
              <span className="payout-row-value">
                {money(currentCustomer.totalSpent)}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Kredit</span>
              <span className="payout-row-value">
                {money(currentCustomer.creditBalance)}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Poslední dokončená jízda
              </span>
              <span className="payout-row-value">
                {formatDate(currentCustomer.lastRideAt)}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Registrace</span>
              <span className="payout-row-value">
                {formatDate(currentCustomer.createdAt)}
              </span>
            </div>
          </div>

          <div className="actions">
            <button
              className="btn-sm btn-gold"
              onClick={() => {
                setDetailModal(false);
                openEdit(currentCustomer);
              }}
            >
              Upravit údaje
            </button>

            <button
              className="btn-sm btn-gold"
              onClick={() => {
                setDetailModal(false);
                openPassword(currentCustomer);
              }}
            >
              Změnit heslo
            </button>

            {!currentCustomer.blocked ? (
              <button
                className="btn-sm btn-red"
                onClick={async () => {
                  await block(currentCustomer.id, true);
                  setDetailModal(false);
                }}
              >
                Blokovat účet
              </button>
            ) : (
              <button
                className="btn-sm btn-green"
                onClick={async () => {
                  await block(currentCustomer.id, false);
                  setDetailModal(false);
                }}
              >
                Odblokovat účet
              </button>
            )}

            <button
              className="btn-sm btn-red"
              onClick={() => deleteAccount(currentCustomer)}
            >
              Smazat účet
            </button>
          </div>
        </Modal>
      )}

      {editModal && (
        <Modal
          title="Editace zákazníka"
          onClose={() => setEditModal(false)}
          actions={
            <>
              <button
                className="btn-gray"
                onClick={() => setEditModal(false)}
              >
                Zrušit
              </button>
              <button className="btn" onClick={saveEdit}>
                Uložit
              </button>
            </>
          }
        >
          <div className="edit-row">
            <label className="edit-label">Jméno</label>
            <input
              value={editForm.name}
              onChange={e =>
                setEditForm({
                  ...editForm,
                  name: e.target.value
                })
              }
            />
          </div>

          <div className="edit-row">
            <label className="edit-label">E-mail</label>
            <input
              value={editForm.email}
              onChange={e =>
                setEditForm({
                  ...editForm,
                  email: e.target.value
                })
              }
            />
          </div>

          <div className="edit-row">
            <label className="edit-label">Telefon</label>
            <input
              value={editForm.phone}
              onChange={e =>
                setEditForm({
                  ...editForm,
                  phone: e.target.value
                })
              }
            />
          </div>
        </Modal>
      )}

      {passwordModal && (
        <Modal
          title="Změna hesla"
          onClose={() => setPasswordModal(false)}
          actions={
            <>
              <button
                className="btn-gray"
                onClick={() => setPasswordModal(false)}
              >
                Zrušit
              </button>
              <button className="btn" onClick={savePassword}>
                Uložit
              </button>
            </>
          }
        >
          <p
            style={{
              color: 'var(--gray)',
              fontSize: 13,
              marginBottom: 16
            }}
          >
            Zákazník: {currentCustomer?.user?.name}
          </p>

          <input
            type="password"
            placeholder="Nové heslo (min. 6 znaků)"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
          />
        </Modal>
      )}
    </div>
  );
}
